import React, { useState, useRef } from 'react';
import {
    AppBar, Toolbar, IconButton, InputBase, Badge,
    Box, Avatar, Stack, Typography, alpha, styled
} from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import { Search as SearchIcon, NotificationsNone as NotificationsIcon, Menu as MenuIcon } from '@mui/icons-material';
import { useAuthStore } from '../../store/useAuthStore';
import { useNotificationStore } from '../../store/useNotificationStore';
import { NotificationPanel } from '../notifications/components/NotificationPanel';

const SearchBox = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: '12px',
    backgroundColor: alpha(theme.palette.common.black, 0.03),
    border: '1px solid transparent',
    transition: 'all 0.2s ease',
    width: '100%',
    maxWidth: 400,
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.black, 0.05),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    },
    [theme.breakpoints.down('sm')]: { maxWidth: '180px' },
}));

const formatRole = (role) => {
    if (!role) return 'Utilisateur';
    return role.replace('ROLE_', '').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
};

const getInitials = (name) => name ? name.substring(0, 2).toUpperCase() : '??';

export function Navbar({ onMenuClick, drawerWidth }) {
    const user = useAuthStore((state) => state.user);
    const unreadCount = useNotificationStore((state) => state.unreadCount);

    const [notifOpen, setNotifOpen] = useState(false);
    const notifBtnRef = useRef(null);

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                    bgcolor: 'white',
                    color: 'text.primary',
                    boxShadow: 'none',
                    borderBottom: '1px solid',
                    borderColor: 'rgba(201,168,76,0.12)',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, sm: 4 } }}>
                    <IconButton color="inherit" edge="start" onClick={onMenuClick} sx={{ mr: 1, display: { sm: 'none' } }}>
                        <MenuIcon />
                    </IconButton>

                    <SearchBox>
                        <Box sx={{ px: '15px', height: '100%', position: 'absolute', display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
                            <SearchIcon fontSize="small" sx={{ color: 'primary.main' }} />
                        </Box>
                        <InputBase
                            placeholder="Rechercher..."
                            sx={{ pl: 6, py: 0.8, width: '100%', fontSize: '0.85rem', '& .MuiInputBase-input': { fontFamily: "'DM Sans', sans-serif" } }}
                        />
                    </SearchBox>

                    <Stack direction="row" spacing={{ xs: 1, sm: 3 }} alignItems="center">
                        {/* Indicateur Live */}
                        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{
                                    width: 6, height: 6, borderRadius: '50%', bgcolor: '#6dd9a8',
                                    boxShadow: '0 0 0 3px rgba(109,217,168,0.2)',
                                    animation: 'pulse 2s infinite',
                                    '@keyframes pulse': {
                                        '0%, 100%': { boxShadow: '0 0 0 3px rgba(109,217,168,0.2)' },
                                        '50%': { boxShadow: '0 0 0 5px rgba(109,217,168,0.05)' },
                                    },
                                }} />
                                <WifiIcon sx={{ fontSize: 12, color: 'success.main' }} />
                            </Box>
                        </Stack>

                        <IconButton
                            ref={notifBtnRef}
                            size="medium"
                            onClick={() => setNotifOpen(!notifOpen)}
                            sx={{
                                borderRadius: '10px',
                                color: notifOpen ? 'primary.main' : 'text.secondary',
                                bgcolor: notifOpen ? alpha('#c9a84c', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#c9a84c', 0.05), color: 'primary.main' }
                            }}
                        >
                            <Badge badgeContent={unreadCount} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 18, minWidth: 18 } }}>
                                <NotificationsIcon fontSize="medium" />
                            </Badge>
                        </IconButton>

                        <Box sx={{ height: 24, width: '1px', bgcolor: 'rgba(0,0,0,0.06)' }} />

                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ bgcolor: 'secondary.main', color: 'primary.main', width: 38, height: 38, fontSize: '0.9rem', fontWeight: 700, border: '2px solid', borderColor: alpha('#c9a84c', 0.2), cursor: 'pointer' }}>
                                {getInitials(user?.username)}
                            </Avatar>
                            <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{user?.username || 'Utilisateur'}</Typography>
                                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.7rem' }}>
                                    {user?.roles?.length > 0 ? formatRole(user.roles[0]) : 'Accès limité'}
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                </Toolbar>
            </AppBar>

            <NotificationPanel anchorEl={notifBtnRef.current} open={notifOpen} onClose={() => setNotifOpen(false)} />
        </>
    );
}